import { afterNextRender, Component, computed, effect, inject, input, untracked, viewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { TrackedLinkDirective } from 'src/app/core/analytics/directives/tracked-link.directive';
import { AcknowledgementType } from 'src/app/core/enums/acknowledgement-type.enum';
import { RecordEventService } from 'src/app/core/services/record-event.service';
import { LanguageService } from 'src/app/core/services/language.service';

import { LegalAcknowledgementDialogComponent } from '../legal-acknowledgement-dialog/legal-acknowledgement-dialog.component';
import { LegalAcknowledgementsStore } from '../../store/legal-acknowledgements.store';

interface TextPart {
  type: 'text';
  content: string;
}

interface LinkPart {
  type: 'link';
  content: string;
  linkType: string;
}

type MessagePart = TextPart | LinkPart;

@Component({
  selector: 'app-legal-acknowledgement-footer',
  templateUrl: './legal-acknowledgement-footer.component.html',
  styles: `
    p,
    a.nova-link {
      color: var(--nova-text-subtle);
      cursor: pointer;
    }
  `,
  imports: [TrackedLinkDirective, LegalAcknowledgementDialogComponent],
})
export class LegalAcknowledgementFooterComponent {
  type = input<'DEFAULT' | 'ESIGNATURE'>('DEFAULT');
  buttonText = input.required<string>();

  private readonly translationService = inject(TranslateService);
  private readonly legalStore = inject(LegalAcknowledgementsStore);
  private readonly recordEventService = inject(RecordEventService);
  private readonly languageService = inject(LanguageService);
  private readonly consentDialog = viewChild.required(LegalAcknowledgementDialogComponent);

  private readonly currentLanguage = this.languageService.currentLanguage;
  private lastLanguage = this.languageService.currentLanguage();

  private readonly acknowledgementTypes = computed(() => {
    return this.type() === 'ESIGNATURE'
      ? [AcknowledgementType.ESignature]
      : [
          AcknowledgementType.PrivacyPolicy,
          AcknowledgementType.TermsOfUse,
          AcknowledgementType.AiDisclaimer,
        ];
  });

  translatedMessage = computed(() => {
    return this.translationService.instant(
      `legal-acknowledgements.MESSAGE-${this.type()}`,
      {
        buttonText: this.buttonText(),
      },
    );
  });

  messageParts = computed<MessagePart[]>(() => {
    const text = this.translatedMessage();
    const parts: MessagePart[] = [];

    const linkRegex = /\[\[(\w+):(.+?)\]\]/g;
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const textBefore = text.substring(lastIndex, match.index);
        if (textBefore) {
          parts.push({ type: 'text', content: textBefore });
        }
      }

      parts.push({
        type: 'link',
        content: match[2],
        linkType: match[1],
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push({ type: 'text', content: remainingText });
      }
    }

    return parts;
  });

  constructor() {
    effect(() => {
      const currentLang = this.currentLanguage();
      if (currentLang === this.lastLanguage){
        return;
      }

      this.lastLanguage = currentLang;
      untracked(() => {
        this.loadAcknowledgements(true);
      });
    });

    afterNextRender(() => {
      this.loadAcknowledgements(false);
    })
  }

  private loadAcknowledgements(forceReload = false): void {
    const types = this.acknowledgementTypes();
    if (types.length === 0) {
      return;
    }

    types.forEach(type => {
      void this.legalStore.loadAcknowledgementDetails(type, forceReload);
    });
  }

  async onLinkClicked(linkType: string): Promise<void> {
    const acknowledgementType = this.mapLinkTypeToAcknowledgementType(linkType);
    if (acknowledgementType) {
      await this.openConsentDialog(acknowledgementType).catch(() => {
        // Silently fail - user clicked a link but dialog couldn't open
      });
    }
  }

  async openConsentDialog(type: AcknowledgementType): Promise<void> {
    const acknowledgement = await this.legalStore.loadAcknowledgementDetails(type);

    const title = this.translationService.instant(`legal-acknowledgements.TITLE-${type}`);
    this.consentDialog().open(title, acknowledgement.content);

    await firstValueFrom(this.recordEventService.recordConsentPresentedEvent(acknowledgement.id)).catch(() => {
      // Audit logging is best-effort; do not block dialog
    });
  }

  private mapLinkTypeToAcknowledgementType(linkType: string): AcknowledgementType | null {
    switch (linkType) {
      case 'termsLink':
        return AcknowledgementType.TermsOfUse;
      case 'privacyPolicyLink':
        return AcknowledgementType.PrivacyPolicy;
      case 'aiDisclaimerLink':
        return AcknowledgementType.AiDisclaimer;
      case 'esignatureLink':
        return AcknowledgementType.ESignature;
      default:
        return null;
    }
  }
}
